import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useParams } from 'react-router-dom'
import { CostSavingChartData, DynamicPriceType, NullablePowerPrice, ThreeSixtySavings } from 'src/Libs/dynamic-power-prices/types'
import { useChartDateBarContext } from '../../../chart-kit/shared/context'
import { useStorageListForMissingFarmData } from 'src/Libs/summary-data/hooks/use-storage-id-to-get-list'
import { requiresAdditionalDataPoints, updateFromToAddAdditionalDataPointToTheLeft } from 'src/Libs/dynamic-power-prices/utils'
import { fetchPowerPrices, fetchThreeSixtySavings } from 'src/Libs/dynamic-power-prices/api/power-price.api'
import {
  fillDataWithEmptyTimestampValues,
  filterOutInvalidTimestamps,
  mergeCostSavingData,
} from 'src/Libs/dynamic-power-prices/utils/cost-saving-chart'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { ApiResult } from '@e3dc-react/shell/Types'
import { Resolution, ResolutionTypes } from '../../../chart-kit/shared/types'
import { fillTimeValuesByResolution, getChartViewForResolution } from '../../../chart-kit/shared/utils/csv-export-fns'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'
import { useFarmContext } from 'src/Elements/Farm'

interface ReturnType {
  handleFetchCostSavingsData: (resolution: Resolution) => Promise<CostSavingChartData[]>
  isFetching: boolean
}

const SHARED_QUERY_KEY = 'cost-savings-data'

/**
 * Exposes logic to fetch cost saving data manually (e.g. triggered by button click).
 */
const useFetchCostSavingDataManual = (): ReturnType => {
  const { from, to, view } = useChartDateBarContext()
  const authFetch = useAuthFetchWithResponse()
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const storagesList: string[] = useStorageListForMissingFarmData(storageId)
  const { isFarm } = useFarmContext()
  const { data: wallboxes } = useWallboxHardwareByStorage()
  const hasWallboxes = wallboxes.length > 0
  const queryClient = useQueryClient()
  const {
    featureFlags: { isNewEmobiltyRouteEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const shouldUseNewRoute = !isFeatureFlagsLoading && isNewEmobiltyRouteEnabled

  const isFetching =
    useIsFetching({
      queryKey: [SHARED_QUERY_KEY, storageId],
    }) > 0

  const handleFetchCostSavingsData = async (resolution: Resolution): Promise<CostSavingChartData[]> => {
    if (!from || !to) return []

    const isResolutionValid = resolution === ResolutionTypes.Day || resolution === ResolutionTypes.Hour || resolution === ResolutionTypes.Month
    const timePeriod = isResolutionValid ? resolution : undefined
    const chartViewForProcessing = getChartViewForResolution(resolution)
    const doesRequireAdditionalDataPoint = requiresAdditionalDataPoints(view, to)
    const fetchFrom = doesRequireAdditionalDataPoint && from ? updateFromToAddAdditionalDataPointToTheLeft(from, view) : from
    const filledTimeValuesForResolution = fillTimeValuesByResolution({ from: from.toDate(), to: to.toDate(), resolution })

    const [powerPriceData, threeSixtyWallboxData, threeSixtyBatteryData] = await Promise.all([
      queryClient
        .fetchQuery({
          queryKey: [SHARED_QUERY_KEY, 'power-prices', installationId, fetchFrom, to, timePeriod],
          queryFn: () => fetchPowerPrices(installationId, authFetch, fetchFrom, to, timePeriod),
        })
        .then((data: ApiResult<NullablePowerPrice[]>) => data?.value ?? []),
      hasWallboxes
        ? queryClient
            .fetchQuery({
              queryKey: [SHARED_QUERY_KEY, 'three-sixty-wallbox-savings', storageId, fetchFrom, to, timePeriod, isFarm, shouldUseNewRoute],
              queryFn: () =>
                fetchThreeSixtySavings(
                  DynamicPriceType.Wallbox,
                  storageId,
                  storagesList,
                  isFarm,
                  authFetch,
                  shouldUseNewRoute,
                  fetchFrom,
                  to,
                  timePeriod
                ),
            })
            .then((data: ApiResult<ThreeSixtySavings[]>) =>
              fillDataWithEmptyTimestampValues(data, chartViewForProcessing, filledTimeValuesForResolution)
            )
        : Promise.resolve([]),
      queryClient
        .fetchQuery({
          queryKey: [SHARED_QUERY_KEY, 'three-sixty-battery-savings', storageId, fetchFrom, to, timePeriod, isFarm, shouldUseNewRoute],
          queryFn: () =>
            fetchThreeSixtySavings(
              DynamicPriceType.Battery,
              storageId,
              storagesList,
              isFarm,
              authFetch,
              shouldUseNewRoute,
              fetchFrom,
              to,
              timePeriod
            ),
        })
        .then((data: ApiResult<ThreeSixtySavings[]>) =>
          fillDataWithEmptyTimestampValues(data, chartViewForProcessing, filledTimeValuesForResolution)
        ),
    ])

    const powerPriceDataInTimeRange = filterOutInvalidTimestamps(powerPriceData ?? [], fetchFrom, to)

    return mergeCostSavingData(powerPriceDataInTimeRange, threeSixtyBatteryData, threeSixtyWallboxData, fetchFrom, to, view, resolution)
  }

  return {
    handleFetchCostSavingsData,
    isFetching,
  }
}

export default useFetchCostSavingDataManual
